import React from 'react'
import { Scrim } from '../scrim'
import { ModalBody } from './components/modal-body'
import { ModalClosableButton } from './components/modal-closable-button'
import { ModalDescription } from './components/modal-description'
import {
  ModalFirstButtonFooter,
  ModalFooter,
  ModalSecondButtonFooter,
} from './components/modal-footer'
import { ModalHeader } from './components/modal-header'
import { ModalIcon } from './components/modal-icon'
import { ModalTitle } from './components/modal-title'
import * as Comp from './modal.styles'
import type { TModalProps } from './types'

const ModalRoot = ({
  children,
  testID,
  variant = 'small',
  visible = false,
}: TModalProps) => {
  const componentProperties = {
    variant: variant,
    testID,
    visible,
  }

  const renderInternalHeaderIcon = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === ModalIcon) {
      return React.cloneElement(child)
    }
  })

  const renderInternalHeaderTitle = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === ModalTitle) {
      return React.cloneElement(child)
    }
  })

  const renderInternalHeaderClosableButton = React.Children.map(
    children,
    child => {
      if (!React.isValidElement(child)) return

      if (React.isValidElement(child) && child.type === ModalClosableButton) {
        return React.cloneElement(child)
      }
    }
  )

  const renderInternalDescription = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === ModalDescription) {
      return React.cloneElement(child)
    }
  })

  const renderInternalBody = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === ModalBody) {
      return React.cloneElement(child)
    }
  })

  const renderInternalFooter = React.Children.map(children, child => {
    if (!React.isValidElement(child)) return

    if (React.isValidElement(child) && child.type === ModalFooter) {
      return React.cloneElement(child)
    }
  })

  if (!visible) {
    return null
  }

  return (
    <Scrim.Root>
      <Comp.Root {...componentProperties}>
        <ModalHeader>
          {renderInternalHeaderIcon}
          {renderInternalHeaderTitle}
          <Comp.ClosableButtonContainer>
            {renderInternalHeaderClosableButton}
          </Comp.ClosableButtonContainer>
        </ModalHeader>
        <Comp.ModalDescriptionContainer testID="first-description-container">
          {renderInternalDescription?.slice(0, 1)}
        </Comp.ModalDescriptionContainer>
        {renderInternalBody}
        <Comp.ModalDescriptionContainer testID="second-description-container">
          {renderInternalDescription?.slice(1, 2)}
        </Comp.ModalDescriptionContainer>
        {renderInternalFooter}
      </Comp.Root>
    </Scrim.Root>
  )
}

export const Modal = {
  Root: ModalRoot,
  HeaderIcon: ModalIcon,
  HeaderTitle: ModalTitle,
  HeaderClosableButton: ModalClosableButton,
  Description: ModalDescription,
  Body: ModalBody,
  Footer: ModalFooter,
  PrimaryButton: ModalFirstButtonFooter,
  SecondaryButton: ModalSecondButtonFooter,
}

export type { TModalProps }
